import React, { useMemo, useContext } from 'react'
import { DataTableContext } from '../components/DataTable'
import moment from 'moment'

// cells
/*
	Used through 'format' function and automatically receive default options from DataTable.
	Return object containing 'Cell' key and others if needed.
	Example of usage:
	{
		format: (defaultOptions = {}) => dateColumn({ ...defaultOptions, ...extendDefaultOptions })
	}
*/
export const dateColumn = (options = {}) => ({ Cell: DateCell(options) })

export const timeColumn = (options = {}) => ({ Cell: TimeCell(options) })

export const numberColumn = (options = {}) => {
	const { aggregateType, alignCellLeft, alignHeaderLeft, alignFooterLeft } = options
	let aggregate
	switch(aggregateType) {
		case 'sum': aggregate = aggregateSum
			break
		default:
	}
	return {
		...!alignCellLeft && { style: { justifyContent: 'flex-end' } },
		...!alignHeaderLeft && { headerStyle: { justifyContent: 'flex-end' } },
		...!alignFooterLeft && { footerStyle: { justifyContent: 'flex-end' } },
		...aggregate && { aggregate: (vals, customOpts = {}) => aggregate(vals, { ...options, ...customOpts }) },
		Cell: NumberCell(options)
	}
}
// ----------- End of cells

// cell values
/*
	Used in 'Cell' key as call of function with format options object argument.
	Return string or React node.
	Example of usage:
	{
		Cell: NumberCell({ locales: 'de-CH' })
	}
*/
export const NumberCell = (options = {}) => ({ value }) => {
	const { toFixed = 2, locales, floatsPrecisionOnly, noRedundantDecimalZero } = options
	if(value === null || typeof value === 'undefined' || isNaN(value)) {
		return null
	}
	const usePrecision = floatsPrecisionOnly ? !Number.isInteger(value) : true
	let formattedValue = Number(value).toLocaleString(locales, { ...usePrecision && {
		minimumFractionDigits: toFixed,
		maximumFractionDigits: toFixed
	} })

	return noRedundantDecimalZero ? parseFloat(formattedValue).toString() : formattedValue
}

export const EmptyCell = () => ''

export const DateTimeCell = (options = {}) => state => {
	if(!state?.value) {
		return ''
	}
	const dateTime = new Date(state?.value).toLocaleDateString(options?.locales,
		{
			timeZone: 'UTC',
			day: '2-digit',
			year: 'numeric',
			month: '2-digit',
			hour: 'numeric',
			minute: 'numeric',
			second: 'numeric'
		})
	return dateTime
}

export const DateCell = (options = {}) => state => {
	if(!state?.value) {
		return ''
	}
	return new Date(state?.value).toLocaleDateString(options?.locales, { timeZone: 'UTC', day: '2-digit', year: 'numeric', month: '2-digit' })
}

export const DateCellFormat = (options = {}) => state => {
	if(!state?.value) {
		return ''
	}
	const isHyphen = state.value.includes(' - ')
	const isColon = state.value.includes(':')
	const isPoint = state.value.includes('.')
	if(isHyphen) {
		let [fromDate, toDate] = state.value.split('-')
		fromDate = moment(fromDate).format('DD.MM.YYYY')
		toDate = moment(toDate).format('DD.MM.YYYY')
		return `${fromDate} - ${toDate}`
	}
	if(isColon) {
		return moment(state?.value.split('T')[0], 'YYYY.MM.DD').format('DD.MM.YYYY')
	}
	if(isPoint) {
		return moment(state?.value, 'DD.MM.YYYY').format('DD.MM.YYYY')
	}
	return moment(state?.value).format('DD.MM.YYYY')
}

export const TimeCell = (options = {}) => ({ value }) => {
	if(!value) {
		return ''
	}
	if(new Date(value).toString() === 'Invalid Date' && value.includes(':')) {
		const paddedValue = new Date(`1970-01-01T${value}`)
		return paddedValue.toLocaleTimeString(options?.locales, { timeZone: 'UTC' })
	}
	return new Date(value).toLocaleTimeString(options?.locales, { timeZone: 'UTC' })
}

export const FooterSum = (options = {}) => {
	const Footer = ({ rows, column }) => {
		const total = useMemo(
			() => rows.reduce((sum, row) => row.values[column.id] + sum, 0),
			[rows, column.id]
		)
		return NumberCell(options)({ value: total })
	}
	return Footer
}

// Used as function without call and options argument
export const EditableCell = ({ value: initialValue, row: { index }, column: { id } }) => {
	// This is a custom function
	const { updateMyData } = useContext(DataTableContext)
	// We need to keep and update the state of the cell normally
	const [value, setValue] = React.useState(initialValue)

	const onChange = e => {
		setValue(e.target.value)
	}

	// We'll only update the external data when the input is blurred
	const onBlur = () => {
		updateMyData(index, id, value)
	}

	// If the initialValue is changed external, sync it up with our state
	React.useEffect(() => {
		setValue(initialValue)
	}, [initialValue])

	return <input value={value} onChange={onChange} onBlur={onBlur} />
}
// ----------- End of cell values

// aggregation
export const toLocal = (val, { locales, toFixed = 2 }) => val === null || isNaN(val)
	? val
	: val.toLocaleString(locales, {
		minimumFractionDigits: toFixed,
		maximumFractionDigits: toFixed
	})
export const aggregateSum = (vals, { toLocale, locales, toFixed }) => {
	const num = vals.reduce((acc, val = 0) => !isNaN(val) ? (acc + Number(val)) : acc, 0)
	// Convert toLocaleString only values which will not be used in calculations (i.e. in footer)
	if(toLocale) {
		return num ? toLocal(num, { locales, toFixed }) : ''
	}
	return num
}
export const aggregateSumV2 = (vals, key, { locales, toFixed }) => {
	const num = vals.reduce((acc, val = 0) => !isNaN(+val[key]) ? (acc + Number(val[key])) : acc, 0)
	if(locales) {
		return num ? toLocal(num, { locales, toFixed }) : ''
	}
	return num
}

export const minuteToHourConverter = (minutes) => {
	const hours = Math.floor(minutes / 60)
	const remainingMinutes = Math.floor(minutes - (hours * 60))
	const formatted = [
		hours.toString().padStart(2, '0'),
		remainingMinutes.toString().padStart(2, '0')
	].join(':')
	return formatted
}

// ----------- End of aggregation
